import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { chatService } from '../../services/chatService';
import { tagService, TagItem } from '../../services/tagService';

interface IngestedDocument {
  id: string;
  filename: string;
  file_path: string | null;
  url: string | null;
  documents_metadata: Record<string, any> | null;
  source: string | null;
  created_at: string;
}

interface UploadedTextEntry {
  id: string;
  text: string;
  fund: string;
  source?: string | null;
  as_of_date: string;
  ingested_date: string;
  created_at: string;
}

type SourceFilter = 'all' | 'files' | 'text';
type PreviewVariant = 'original' | 'processed';

interface SheetData {
  name: string;
  rows: string[][];
}

const KnowledgeDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<IngestedDocument[]>([]);
  const [texts, setTexts] = useState<UploadedTextEntry[]>([]);
  const [tagFields, setTagFields] = useState<TagItem[]>([]);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [viewText, setViewText] = useState<UploadedTextEntry | null>(null);
  const [editText, setEditText] = useState<UploadedTextEntry | null>(null);
  const [editTextValue, setEditTextValue] = useState('');
  const [editTextFund, setEditTextFund] = useState('');
  const [editTextAsOfDate, setEditTextAsOfDate] = useState('');
  const [editTextSaving, setEditTextSaving] = useState(false);
  const [editTextError, setEditTextError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [previewDoc, setPreviewDoc] = useState<IngestedDocument | null>(null);
  const [previewVariant, setPreviewVariant] = useState<PreviewVariant>('original');
  const [previewVariantInfo, setPreviewVariantInfo] = useState<{
    original_available: boolean;
    processed_available: boolean;
    original_name: string;
    processed_name: string;
  }>({
    original_available: true,
    processed_available: false,
    original_name: '',
    processed_name: '',
  });
  const [previewDisplayName, setPreviewDisplayName] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState('');
  const [previewSheets, setPreviewSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [editDoc, setEditDoc] = useState<IngestedDocument | null>(null);
  const [editMeta, setEditMeta] = useState<Record<string, string>>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  /** Metadata / tag filter: combobox with autocomplete (distinct values from library). */
  const [metadataComboValue, setMetadataComboValue] = useState('');
  const [metadataComboOpen, setMetadataComboOpen] = useState(false);
  const metadataComboRef = useRef<HTMLDivElement>(null);

  /** Free-text search: filename, origin, full text content, etc. */
  const [freeTextSearch, setFreeTextSearch] = useState('');
  /** Inclusive date range (YYYY-MM-DD); compared to doc metadata date or created_at. */
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  type SortKey = 'filename' | 'metadata' | 'ingested' | 'asOf';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir | null>(null);

  const cycleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('desc');
      return;
    }
    if (sortDir === 'desc') {
      setSortDir('asc');
      return;
    }
    setSortKey(null);
    setSortDir(null);
  };

  const defaultTagField = tagFields.find((t) => t.is_default)?.tag_name || 'fund';
  const defaultTagValues = tagFields.find((t) => t.tag_name === defaultTagField)?.tag_values || [];

  const fetchDocuments = useCallback(async () => {
    try {
      const [docsData, textData, tagsData] = await Promise.all([
        chatService.getIngestedDocuments(),
        chatService.getUploadedTextEntries(),
        tagService.listTags({ is_active: true }),
      ]);
      setDocuments(Array.isArray(docsData) ? docsData : []);
      setTexts(Array.isArray(textData) ? textData : []);
      setTagFields(Array.isArray(tagsData) ? tagsData : []);
    } catch {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (metadataComboRef.current && !metadataComboRef.current.contains(e.target as Node)) {
        setMetadataComboOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const getMetaTagValue = (doc: IngestedDocument): string => {
    const meta = doc.documents_metadata || {};
    for (const t of tagFields) {
      if (meta[t.tag_name] !== undefined && meta[t.tag_name] !== null && meta[t.tag_name] !== '') {
        return String(meta[t.tag_name]);
      }
    }
    return meta.TAG || meta.fund || meta.tag || meta.RAG_tag || '-';
  };

  const getMetaDate = (doc: IngestedDocument): string => {
    const meta = doc.documents_metadata || {};
    return meta.ingested_date || '-';
  };

  const getMetaAsOfDate = (doc: IngestedDocument): string => {
    const meta = doc.documents_metadata || {};
    return meta.as_of_date || '-';
  };

  /** Humanize unknown `source` strings from the API (e.g. migration scripts). */
  const formatSourceCode = (code: string): string =>
    code
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

  /**
   * Where the row came from: `ingested_documents.source` (and migration scripts set values like
   * `migration_data_folder`). Only empty / missing / explicit `ui` → "UI"; unknown codes are shown
   * as-is (humanized), never as "UI".
   */
  const getOriginLabel = (source: string | null | undefined): { label: string; color: string } => {
    const s = typeof source === 'string' ? source.trim() : '';
    if (!s || s === 'ui') {
      return { label: 'UI', color: 'bg-slate-50 text-slate-700' };
    }
    switch (s) {
      case 'feedback_agent':
        return { label: 'Feedback agent', color: 'bg-amber-50 text-amber-700' };
      case 'ingest_agent':
        return { label: 'Ingest agent', color: 'bg-teal-50 text-teal-700' };
      case 'migration_data_folder':
        return { label: 'Migration (data folder)', color: 'bg-indigo-50 text-indigo-800' };
      case 'migration_local':
        return { label: 'Migration (local)', color: 'bg-indigo-50 text-indigo-800' };
      case 'migration_pinecone_text':
        return { label: 'Migration (Pinecone text)', color: 'bg-indigo-50 text-indigo-800' };
      case 'migration':
        return { label: 'Migration', color: 'bg-indigo-50 text-indigo-800' };
      case 'db':
        return { label: 'Migration (DB)', color: 'bg-indigo-50 text-indigo-800' };
      case 'pinecone':
        return { label: 'Migration (Pinecone)', color: 'bg-indigo-50 text-indigo-800' };
      default:
        return { label: formatSourceCode(s), color: 'bg-gray-100 text-gray-800' };
    }
  };

  /** Prefer column `source`; some rows only duplicate it under `documents_metadata`. */
  const resolveDocumentOriginSource = (doc: IngestedDocument): string | null => {
    const top = doc.source;
    if (top != null && String(top).trim() !== '') {
      return String(top);
    }
    const meta = doc.documents_metadata;
    const nested = meta && typeof meta === 'object' && 'source' in meta ? (meta as { source?: unknown }).source : undefined;
    if (typeof nested === 'string' && nested.trim() !== '') {
      return nested;
    }
    return null;
  };

  /** YYYY-MM-DD for range filter: ingested date from metadata, else created_at. */
  const getDocumentDayString = (doc: IngestedDocument): string => {
    const meta = doc.documents_metadata || {};
    const raw = meta.ingested_date;
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw.trim())) {
      return raw.trim().slice(0, 10);
    }
    return doc.created_at ? doc.created_at.slice(0, 10) : '';
  };

  const getTextDayString = (entry: UploadedTextEntry): string =>
    entry.ingested_date || (entry.created_at ? entry.created_at.slice(0, 10) : '');

  const TYPE_FILE_BADGE = 'bg-blue-50 text-blue-700';
  const TYPE_TEXT_BADGE = 'bg-purple-50 text-purple-700';

  const isSpreadsheet = (filename: string): boolean => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(ext);
  };

  const parseSpreadsheet = async (blob: Blob): Promise<SheetData[]> => {
    const arrayBuffer = await blob.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    return workbook.SheetNames.map((name) => {
      const ws = workbook.Sheets[name];
      const rows: string[][] = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        defval: '',
        raw: false,
      }) as string[][];
      return { name, rows };
    });
  };

  const getPreviewNameForVariant = (
    doc: IngestedDocument,
    variant: PreviewVariant,
    info: {
      original_name?: string;
      processed_name?: string;
    },
  ): string => {
    if (variant === 'processed') {
      if (info.processed_name) return info.processed_name;
      const dotIdx = doc.filename.lastIndexOf('.');
      const stem = dotIdx >= 0 ? doc.filename.slice(0, dotIdx) : doc.filename;
      return `${stem}_processed.pdf`;
    }
    return info.original_name || doc.filename;
  };

  const loadPreviewVariant = async (
    doc: IngestedDocument,
    variant: PreviewVariant,
    info: {
      original_available: boolean;
      processed_available: boolean;
      original_name: string;
      processed_name: string;
    },
  ) => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewTextContent('');
    setPreviewSheets([]);
    setActiveSheet(0);
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const blob = await chatService.getIngestedDocumentFileByVariant(doc.id, variant);
      const displayName = getPreviewNameForVariant(doc, variant, info);
      setPreviewDisplayName(displayName);
      setPreviewVariant(variant);
      if (isSpreadsheet(displayName)) {
        const sheets = await parseSpreadsheet(blob);
        setPreviewSheets(sheets);
      } else {
        const ext = displayName.split('.').pop()?.toLowerCase() || '';
        if (['txt', 'md', 'log', 'json', 'csv'].includes(ext)) {
          const text = await blob.text();
          setPreviewTextContent(text);
        }
      }
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to load file for preview';
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = async (doc: IngestedDocument) => {
    setPreviewDoc(doc);
    setPreviewVariant('original');
    setPreviewVariantInfo({
      original_available: true,
      processed_available: false,
      original_name: doc.filename,
      processed_name: '',
    });
    setPreviewDisplayName(doc.filename);
    setPreviewError('');
    try {
      const variants = await chatService.getIngestedDocumentFileVariants(doc.id).catch(() => ({
        file_id: doc.id,
        original_available: true,
        processed_available: false,
        original_name: doc.filename,
        processed_name: '',
      }));
      setPreviewVariantInfo({
        original_available: variants.original_available,
        processed_available: variants.processed_available,
        original_name: variants.original_name || doc.filename,
        processed_name: variants.processed_name || '',
      });

      if (!variants.original_available && !variants.processed_available) {
        setPreviewError('No preview variant is available for this document');
        return;
      }

      const initialVariant: PreviewVariant = variants.original_available ? 'original' : 'processed';
      await loadPreviewVariant(doc, initialVariant, {
        original_available: variants.original_available,
        processed_available: variants.processed_available,
        original_name: variants.original_name || doc.filename,
        processed_name: variants.processed_name || '',
      });
    } catch {
      setPreviewError('Failed to load file for preview');
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewDoc(null);
    setPreviewVariant('original');
    setPreviewVariantInfo({
      original_available: true,
      processed_available: false,
      original_name: '',
      processed_name: '',
    });
    setPreviewDisplayName('');
    setPreviewBlobUrl(null);
    setPreviewTextContent('');
    setPreviewSheets([]);
    setActiveSheet(0);
    setPreviewError('');
    setPreviewLoading(false);
  };

  const handleDownloadFromPreview = () => {
    if (!previewDoc || !previewBlobUrl) return;
    const a = document.createElement('a');
    a.href = previewBlobUrl;
    a.download = previewDisplayName || previewDoc.filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (doc: IngestedDocument) => {
    const confirmed = window.confirm(
      `Delete "${doc.filename}" from Knowledge Library?`,
    );
    if (!confirmed) return;

    setDeletingId(doc.id);
    try {
      await chatService.deleteIngestedDocument(doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch {
      alert('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteText = async (textEntry: UploadedTextEntry) => {
    const confirmed = window.confirm(
      `Delete this uploaded text from Knowledge Library?`,
    );
    if (!confirmed) return;

    setDeletingId(textEntry.id);
    try {
      await chatService.deleteUploadedTextEntry(textEntry.id);
      setTexts((prev) => prev.filter((t) => t.id !== textEntry.id));
    } catch {
      alert('Failed to delete uploaded text');
    } finally {
      setDeletingId(null);
    }
  };

  const openEditText = (textEntry: UploadedTextEntry) => {
    setEditText(textEntry);
    setEditTextValue(textEntry.text);
    setEditTextFund(textEntry.fund || '');
    setEditTextAsOfDate(textEntry.as_of_date || new Date().toISOString().slice(0, 10));
    setEditTextError('');
  };

  const handleSaveText = async () => {
    if (!editText) return;
    const textValue = editTextValue.trim();
    const fundValue = editTextFund.trim();
    if (!textValue || !fundValue) {
      setEditTextError('Text and metadata are required');
      return;
    }

    setEditTextSaving(true);
    setEditTextError('');
    try {
      const updated = await chatService.updateUploadedTextEntry(editText.id, {
        text: textValue,
        fund: fundValue,
        as_of_date: editTextAsOfDate,
        metadata_tags: {
          [defaultTagField]: fundValue,
        },
      });
      setTexts((prev) =>
        prev.map((entry) =>
          entry.id === editText.id
            ? {
                ...entry,
                text: updated.text,
                fund: updated.fund,
                created_at: updated.created_at || entry.created_at,
                as_of_date: updated.as_of_date || entry.as_of_date,
                ingested_date: updated.ingested_date || entry.ingested_date,
              }
            : entry,
        ),
      );
      setEditText(null);
    } catch (err: any) {
      setEditTextError(err?.response?.data?.detail || 'Failed to update text');
    } finally {
      setEditTextSaving(false);
    }
  };

  const openEditMeta = (doc: IngestedDocument) => {
    const meta = doc.documents_metadata || {};
    const editableFields: Record<string, string> = {};
    for (const t of tagFields) {
      editableFields[t.tag_name] = meta[t.tag_name] !== undefined ? String(meta[t.tag_name]) : '';
    }
    editableFields['as_of_date'] = meta.as_of_date ? String(meta.as_of_date) : new Date().toISOString().slice(0, 10);
    setEditMeta(editableFields);
    setEditDoc(doc);
    setEditError('');
  };

  const handleSaveMetadata = async () => {
    if (!editDoc) return;
    setEditSaving(true);
    setEditError('');
    try {
      const nonEmpty: Record<string, string> = {};
      for (const [k, v] of Object.entries(editMeta)) {
        if (v) nonEmpty[k] = v;
      }
      const result = await chatService.updateDocumentMetadata(editDoc.id, nonEmpty);
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === editDoc.id ? { ...d, documents_metadata: result.documents_metadata } : d,
        ),
      );
      setEditDoc(null);
    } catch (err: any) {
      setEditError(err?.response?.data?.detail || 'Failed to update metadata');
    } finally {
      setEditSaving(false);
    }
  };

  const getTagValuesForField = (fieldName: string): string[] => {
    const tag = tagFields.find((t) => t.tag_name === fieldName);
    return tag?.tag_values || [];
  };

  /** Distinct scalar metadata / tag strings for combobox suggestions. */
  const metadataAutocompleteOptions = useMemo(() => {
    const acc = new Set<string>();
    const push = (v: unknown) => {
      if (v == null) return;
      const s = String(v).trim();
      if (s.length === 0 || s.length > 400) return;
      acc.add(s);
    };

    // Only include configured tag values from Metadata Tags Configuration.
    tagFields.forEach((tagField) => {
      (tagField.tag_values || []).forEach((v) => push(v));
    });

    return Array.from(acc).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [tagFields]);

  const metadataComboSuggestions = useMemo(() => {
    const q = metadataComboValue.trim().toLowerCase();
    const list = q
      ? metadataAutocompleteOptions.filter((opt) => opt.toLowerCase().includes(q))
      : metadataAutocompleteOptions;
    return list.slice(0, 100);
  }, [metadataAutocompleteOptions, metadataComboValue]);

  const filteredDocuments = useMemo(() => {
    const metaQ = metadataComboValue.trim().toLowerCase();
    const freeQ = freeTextSearch.trim().toLowerCase();

    return documents.filter((doc) => {
      if (metaQ) {
        const m = doc.documents_metadata || {};
        const configuredTagValues = tagFields
          .map((t) => m[t.tag_name])
          .filter((v) => v != null && typeof v !== 'object')
          .map((v) => String(v).toLowerCase());
        if (!configuredTagValues.some((v) => v.includes(metaQ))) return false;
      }

      if (freeQ) {
        const metaTag = getMetaTagValue(doc);
        const parts: string[] = [doc.filename || '', metaTag];
        const m = doc.documents_metadata;
        if (m && typeof m === 'object') {
          try {
            parts.push(JSON.stringify(m));
          } catch {
            /* ignore */
          }
        }
        const origin = getOriginLabel(resolveDocumentOriginSource(doc));
        parts.push(origin.label);
        const hay = parts.join(' ').toLowerCase();
        if (!hay.includes(freeQ)) return false;
      }

      const day = getDocumentDayString(doc);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [documents, metadataComboValue, freeTextSearch, dateFrom, dateTo, tagFields]);

  const filteredTexts = useMemo(() => {
    const metaQ = metadataComboValue.trim().toLowerCase();
    const freeQ = freeTextSearch.trim().toLowerCase();

    return texts.filter((entry) => {
      if (metaQ) {
        const fund = (entry.fund || '').toLowerCase();
        if (!fund.includes(metaQ)) return false;
      }

      if (freeQ) {
        const hay = `${entry.text || ''} ${entry.fund || ''}`.toLowerCase();
        if (!hay.includes(freeQ)) return false;
      }

      const day = getTextDayString(entry);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [texts, metadataComboValue, freeTextSearch, dateFrom, dateTo]);

  const showFiles = sourceFilter === 'all' || sourceFilter === 'files';
  const showText = sourceFilter === 'all' || sourceFilter === 'text';

  const getDocSortValue = (doc: IngestedDocument, key: SortKey): string => {
    const meta = doc.documents_metadata || {};
    if (key === 'filename') {
      return (doc.filename || '').trim().toLowerCase();
    }
    if (key === 'metadata') {
      return String(getMetaTagValue(doc) || '').trim().toLowerCase();
    }
    if (key === 'ingested') {
      const raw = meta.ingested_date;
      if (typeof raw === 'string' && raw.trim() !== '') return raw.trim().slice(0, 10);
      return doc.created_at ? doc.created_at.slice(0, 10) : '';
    }
    const asOf = meta.as_of_date;
    return typeof asOf === 'string' ? asOf.trim().slice(0, 10) : '';
  };

  const getTextSortValue = (entry: UploadedTextEntry, key: SortKey): string => {
    if (key === 'filename') {
      return (entry.text || '').trim().toLowerCase();
    }
    if (key === 'metadata') {
      return (entry.fund || '').trim().toLowerCase();
    }
    if (key === 'ingested') {
      return entry.ingested_date || (entry.created_at ? entry.created_at.slice(0, 10) : '');
    }
    return entry.as_of_date || '';
  };

  const sortedDocuments = useMemo(() => {
    if (!sortKey || !sortDir) return filteredDocuments;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredDocuments].sort((a, b) => {
      const av = getDocSortValue(a, sortKey);
      const bv = getDocSortValue(b, sortKey);
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    });
  }, [filteredDocuments, sortKey, sortDir]);

  const sortedTexts = useMemo(() => {
    if (!sortKey || !sortDir) return filteredTexts;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredTexts].sort((a, b) => {
      const av = getTextSortValue(a, sortKey);
      const bv = getTextSortValue(b, sortKey);
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    });
  }, [filteredTexts, sortKey, sortDir]);

  const visibleDocuments = showFiles ? sortedDocuments : [];
  const visibleTextRows = showText ? sortedTexts : [];

  const poolCount =
    (showFiles ? documents.length : 0) + (showText ? texts.length : 0);
  const visibleCount = visibleDocuments.length + visibleTextRows.length;

  const hasSearchOrDate =
    Boolean(metadataComboValue.trim()) ||
    Boolean(freeTextSearch.trim()) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const clearFilters = () => {
    setMetadataComboValue('');
    setFreeTextSearch('');
    setDateFrom('');
    setDateTo('');
    setMetadataComboOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>;
  }

  const showNoFilterResults =
    visibleCount === 0 && poolCount > 0 && hasSearchOrDate;

  return (
    <div className="w-full min-w-0">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6 mb-5">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 tracking-tight">Document Library</h3>
          <p className="text-xs text-gray-500 mt-1">
            {documents.length === 0 && texts.length === 0 ? (
              'No items yet'
            ) : (
              <>
                <span className="font-medium text-gray-800">{visibleCount}</span> shown
                {hasSearchOrDate && poolCount > visibleCount ? (
                  <span className="text-gray-500"> of {poolCount} in this tab</span>
                ) : null}
                {sourceFilter !== 'all' ? (
                  <span className="text-gray-400"> · {documents.length + texts.length} total in library</span>
                ) : null}
              </>
            )}
          </p>
        </div>
        <div className="inline-flex flex-shrink-0 rounded-lg border border-gray-200 overflow-hidden text-xs shadow-sm">
          <button
            type="button"
            onClick={() => setSourceFilter('all')}
            className={`px-3.5 py-2 font-medium transition-colors ${
              sourceFilter === 'all' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setSourceFilter('files')}
            className={`px-3.5 py-2 font-medium border-l border-gray-200 transition-colors ${
              sourceFilter === 'files' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Files
          </button>
          <button
            type="button"
            onClick={() => setSourceFilter('text')}
            className={`px-3.5 py-2 font-medium border-l border-gray-200 transition-colors ${
              sourceFilter === 'text' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Text
          </button>
        </div>
      </div>

      {documents.length > 0 || texts.length > 0 ? (
        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:items-end rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50/90 to-white p-4 shadow-sm">
          <div className="sm:col-span-2 lg:col-span-3 min-w-0">
            <label htmlFor="knowledge-doc-search" className="block text-xs font-medium text-gray-600 mb-1.5">
              Search
            </label>
            <input
              id="knowledge-doc-search"
              type="search"
              value={freeTextSearch}
              onChange={(e) => setFreeTextSearch(e.target.value)}
              placeholder="Filename, origin, body text…"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 min-w-0 relative" ref={metadataComboRef}>
            <label htmlFor="knowledge-meta-combo" className="block text-xs font-medium text-gray-600 mb-1.5">
              Metadata
            </label>
            <div className="relative">
              <input
                id="knowledge-meta-combo"
                type="text"
                value={metadataComboValue}
                onChange={(e) => {
                  setMetadataComboValue(e.target.value);
                  setMetadataComboOpen(true);
                }}
                onFocus={() => setMetadataComboOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setMetadataComboOpen(false);
                }}
                placeholder="Type or pick tag / metadata…"
                autoComplete="off"
                role="combobox"
                aria-expanded={metadataComboOpen}
                aria-controls="knowledge-meta-listbox"
                aria-autocomplete="list"
                className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-20 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25"
              />
              <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
                {metadataComboValue.trim() !== '' && (
                  <button
                    type="button"
                    tabIndex={-1}
                    className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Clear metadata filter"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setMetadataComboValue('');
                      setMetadataComboOpen(false);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  tabIndex={-1}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Show metadata suggestions"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setMetadataComboOpen((o) => !o)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              {metadataComboOpen && (
                <ul
                  id="knowledge-meta-listbox"
                  role="listbox"
                  className="absolute z-40 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5"
                >
                  {metadataComboSuggestions.length === 0 ? (
                    <li className="px-3 py-2.5 text-xs text-gray-500">No matching metadata values</li>
                  ) : (
                    metadataComboSuggestions.map((opt) => (
                      <li
                        key={opt}
                        role="option"
                        className="cursor-pointer px-3 py-2 text-sm text-gray-800 hover:bg-blue-50 active:bg-blue-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setMetadataComboValue(opt);
                          setMetadataComboOpen(false);
                        }}
                      >
                        <span className="line-clamp-2 break-words">{opt}</span>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="knowledge-doc-from" className="block text-xs font-medium text-gray-600 mb-1.5">
              Date from
            </label>
            <input
              id="knowledge-doc-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25"
            />
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="knowledge-doc-to" className="block text-xs font-medium text-gray-600 mb-1.5">
              Date to
            </label>
            <input
              id="knowledge-doc-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-1 flex justify-start lg:justify-end lg:pb-0.5">
            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasSearchOrDate}
              className="w-full lg:w-auto rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      {documents.length === 0 && texts.length === 0 ? (
        <div className="text-center py-16 text-gray-400 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <p className="text-sm font-medium text-gray-500">No files or uploaded text yet</p>
          <p className="text-xs mt-1 text-gray-400">Add files or paste text from the Add Knowledge tab.</p>
        </div>
      ) : showNoFilterResults ? (
        <div className="rounded-xl border border-dashed border-amber-200/80 bg-amber-50/40 py-14 text-center">
          <p className="text-sm font-medium text-gray-700">No documents match your search or date range.</p>
          <p className="text-xs text-gray-500 mt-1">Try different keywords or widen the dates.</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/90">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-[88px]">Type</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-[140px]">Origin</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[220px]">
                  <button
                    type="button"
                    onClick={() => cycleSort('filename')}
                    className="inline-flex items-center gap-1 uppercase tracking-wide text-xs font-semibold text-gray-600 hover:text-blue-700 focus:outline-none"
                    aria-label={`Sort by file name (${sortKey === 'filename' ? sortDir : 'none'})`}
                  >
                    Filename / content
                    {sortKey === 'filename' && (
                      <span className="text-[10px] leading-none text-blue-600">
                        {sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[160px]">
                  <button
                    type="button"
                    onClick={() => cycleSort('metadata')}
                    className="inline-flex items-center gap-1 uppercase tracking-wide text-xs font-semibold text-gray-600 hover:text-blue-700 focus:outline-none"
                    aria-label={`Sort by metadata (${sortKey === 'metadata' ? sortDir : 'none'})`}
                  >
                    Metadata
                    {sortKey === 'metadata' && (
                      <span className="text-[10px] leading-none text-blue-600">
                        {sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-[120px]">
                  <button
                    type="button"
                    onClick={() => cycleSort('ingested')}
                    className="inline-flex items-center gap-1 uppercase tracking-wide text-xs font-semibold text-gray-600 hover:text-blue-700 focus:outline-none"
                    aria-label={`Sort by ingested date (${sortKey === 'ingested' ? sortDir : 'none'})`}
                  >
                    Ingested date
                    {sortKey === 'ingested' && (
                      <span className="text-[10px] leading-none text-blue-600">
                        {sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-[120px]">
                  <button
                    type="button"
                    onClick={() => cycleSort('asOf')}
                    className="inline-flex items-center gap-1 uppercase tracking-wide text-xs font-semibold text-gray-600 hover:text-blue-700 focus:outline-none"
                    aria-label={`Sort by as of date (${sortKey === 'asOf' ? sortDir : 'none'})`}
                  >
                    As of date
                    {sortKey === 'asOf' && (
                      <span className="text-[10px] leading-none text-blue-600">
                        {sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-[200px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocuments.map((doc) => {
                const metaTag = getMetaTagValue(doc);
                const metaDate = getMetaDate(doc);
                const asOfDate = getMetaAsOfDate(doc);
                const origin = getOriginLabel(resolveDocumentOriginSource(doc));
                return (
                  <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
                    <td className="py-3 px-4 align-middle">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${TYPE_FILE_BADGE}`}>File</span>
                    </td>
                    <td className="py-3 px-4 align-middle">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${origin.color}`}>{origin.label}</span>
                    </td>
                    <td className="py-3 px-4 align-middle min-w-0 max-w-xl">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <span className="text-gray-800 text-sm leading-snug line-clamp-2 break-words" title={doc.filename}>
                          {doc.filename}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 align-middle min-w-0 max-w-xs">
                      <span className="inline-block max-w-full truncate px-2 py-0.5 bg-gray-100 rounded-md text-xs text-gray-700" title={metaTag}>
                        {metaTag}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-xs whitespace-nowrap align-middle">
                      {metaDate !== '-' ? metaDate : new Date(doc.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-xs whitespace-nowrap align-middle">
                      {asOfDate !== '-' ? asOfDate : '-'}
                    </td>
                    <td className="py-3 px-4 text-right align-middle">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => openEditMeta(doc)}
                          className="text-xs text-gray-600 hover:text-blue-700 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openPreview(doc)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          disabled={deletingId === doc.id}
                          className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                        >
                          {deletingId === doc.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleTextRows.map((entry) => (
                <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
                  <td className="py-3 px-4 align-middle">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${TYPE_TEXT_BADGE}`}>Text</span>
                  </td>
                  <td className="py-3 px-4 align-middle">
                    {(() => {
                      const origin = getOriginLabel(entry.source);
                      return (
                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${origin.color}`}>
                          {origin.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-3 px-4 text-gray-800 min-w-0 max-w-xl align-middle">
                    <span className="line-clamp-2 text-sm leading-snug break-words" title={entry.text}>
                      {entry.text.slice(0, 280)}{entry.text.length > 280 ? '…' : ''}
                    </span>
                  </td>
                  <td className="py-3 px-4 align-middle min-w-0 max-w-xs">
                    <span className="inline-block max-w-full truncate px-2 py-0.5 bg-gray-100 rounded-md text-xs text-gray-700" title={entry.fund}>
                      {entry.fund || '-'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600 text-xs whitespace-nowrap align-middle">
                    {entry.ingested_date || new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-gray-600 text-xs whitespace-nowrap align-middle">
                    {entry.as_of_date || '-'}
                  </td>
                  <td className="py-3 px-4 text-right align-middle">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => openEditText(entry)}
                        className="text-xs text-gray-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setViewText(entry)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteText(entry)}
                        disabled={deletingId === entry.id}
                        className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        {deletingId === entry.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visibleCount === 0 && poolCount === 0 && !showNoFilterResults ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-sm text-gray-500">
                    {sourceFilter === 'files' && documents.length === 0
                      ? 'No file uploads yet. Switch to Text or All, or add files from Add Knowledge.'
                      : sourceFilter === 'text' && texts.length === 0
                        ? 'No pasted text yet. Switch to Files or All, or add text from Add Knowledge.'
                        : 'Nothing to show in this view.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* File preview modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={closePreview}>
          <div className="bg-white rounded-xl shadow-xl w-[96vw] max-w-7xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 truncate" title={previewDisplayName || previewDoc.filename}>
                  {previewDisplayName || previewDoc.filename}
                </h4>
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => loadPreviewVariant(previewDoc, 'original', previewVariantInfo)}
                    disabled={previewLoading || !previewVariantInfo.original_available}
                    className={`px-2.5 py-1 font-medium transition-colors disabled:opacity-40 ${
                      previewVariant === 'original'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Original
                  </button>
                  <button
                    type="button"
                    onClick={() => loadPreviewVariant(previewDoc, 'processed', previewVariantInfo)}
                    disabled={previewLoading || !previewVariantInfo.processed_available}
                    className={`px-2.5 py-1 font-medium border-l border-gray-200 transition-colors disabled:opacity-40 ${
                      previewVariant === 'processed'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Processed
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                <button
                  onClick={handleDownloadFromPreview}
                  disabled={!previewBlobUrl}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Download
                </button>
                <button onClick={closePreview} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
              {previewLoading && (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
                </div>
              )}
              {previewError && (
                <div className="flex flex-col items-center justify-center h-64 gap-3 text-center p-6">
                  <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="text-sm text-red-600">{previewError}</p>
                </div>
              )}
              {!previewLoading && !previewError && (() => {
                const filenameForPreview = previewDisplayName || previewDoc.filename;
                const ext = filenameForPreview.split('.').pop()?.toLowerCase() || '';
                const isPdf = ext === 'pdf';
                const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
                const isSheet = isSpreadsheet(filenameForPreview);
                const isTextLike = ['txt', 'md', 'log', 'json', 'csv'].includes(ext);

                if (isPdf && previewBlobUrl) {
                  return (
                    <iframe
                      src={previewBlobUrl}
                      className="w-full h-full min-h-[70vh]"
                      title={filenameForPreview}
                    />
                  );
                }

                if (isImage && previewBlobUrl) {
                  return (
                    <div className="flex items-center justify-center h-full min-h-[70vh] p-4 bg-gray-50">
                      <img
                        src={previewBlobUrl}
                        alt={filenameForPreview}
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    </div>
                  );
                }

                if (isSheet && previewSheets.length > 0) {
                  const sheet = previewSheets[activeSheet];
                  const headerRow = sheet.rows[0] ?? [];
                  const dataRows = sheet.rows.slice(1);
                  return (
                    <div className="flex flex-col flex-1 min-h-0">
                      {/* Sheet tabs — only shown when workbook has multiple sheets */}
                      {previewSheets.length > 1 && (
                        <div className="flex gap-1 px-4 pt-3 pb-0 border-b border-gray-200 overflow-x-auto flex-shrink-0">
                          {previewSheets.map((s, i) => (
                            <button
                              key={s.name}
                              onClick={() => setActiveSheet(i)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-t-md border border-b-0 whitespace-nowrap transition-colors ${
                                i === activeSheet
                                  ? 'bg-white text-blue-700 border-gray-200'
                                  : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-100'
                              }`}
                            >
                              {s.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Row / column count summary */}
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {dataRows.length} row{dataRows.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {headerRow.length} column{headerRow.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {/* Scrollable table */}
                      <div className="flex-1 overflow-auto min-h-0">
                        <table className="text-xs border-collapse min-w-full">
                          <thead className="sticky top-0 z-10">
                            <tr>
                              {/* Row-number gutter */}
                              <th className="w-10 bg-gray-100 border border-gray-200 px-2 py-1.5 text-center text-gray-400 font-medium select-none">#</th>
                              {headerRow.map((cell, ci) => (
                                <th
                                  key={ci}
                                  className="bg-gray-100 border border-gray-200 px-3 py-1.5 text-left text-gray-700 font-semibold whitespace-nowrap"
                                >
                                  {cell !== '' ? String(cell) : (
                                    <span className="text-gray-300">{String.fromCharCode(65 + ci)}</span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dataRows.map((row, ri) => (
                              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                                <td className="border border-gray-100 px-2 py-1 text-center text-gray-300 select-none">{ri + 1}</td>
                                {headerRow.map((_, ci) => (
                                  <td
                                    key={ci}
                                    className="border border-gray-100 px-3 py-1 text-gray-700 whitespace-nowrap max-w-[240px] truncate"
                                    title={row[ci] !== undefined ? String(row[ci]) : ''}
                                  >
                                    {row[ci] !== undefined && row[ci] !== '' ? String(row[ci]) : ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }

                if (isTextLike && previewTextContent) {
                  return (
                    <div className="h-full min-h-[70vh] p-4 bg-gray-50 overflow-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                        {previewTextContent}
                      </pre>
                    </div>
                  );
                }

                // File type that cannot be rendered inline — prompt download
                return (
                  <div className="flex flex-col items-center justify-center h-64 gap-4 text-center p-6">
                    <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                      <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500">
                      Preview is not available for <span className="font-medium">.{ext}</span> files.
                    </p>
                    <button
                      onClick={handleDownloadFromPreview}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                      </svg>
                      Download to view
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* View text modal */}
      {viewText && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewText(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[90vw] max-w-3xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Uploaded Text</h4>
              <button onClick={() => setViewText(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[70vh]">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">{viewText.text}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Edit text modal */}
      {editText && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditText(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[90vw] max-w-3xl max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Edit Uploaded Text</h4>
              <button onClick={() => setEditText(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{defaultTagField}</label>
                {defaultTagValues.length > 0 ? (
                  <select
                    value={editTextFund}
                    onChange={(e) => setEditTextFund(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Select —</option>
                    {defaultTagValues.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editTextFund}
                    onChange={(e) => setEditTextFund(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">As of date</label>
                <input
                  type="date"
                  value={editTextAsOfDate}
                  onChange={(e) => setEditTextAsOfDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Knowledge Text</label>
                <textarea
                  value={editTextValue}
                  onChange={(e) => setEditTextValue(e.target.value)}
                  rows={10}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {editTextError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{editTextError}</p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setEditText(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={editTextSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveText}
                disabled={editTextSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editTextSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit metadata modal */}
      {editDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditDoc(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Edit Metadata</h3>
              <p className="text-xs text-gray-500 mt-1 truncate">{editDoc.filename}</p>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {Object.entries(editMeta).map(([key, value]) => {
                const allowedValues = getTagValuesForField(key);
                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{key}</label>
                    {allowedValues.length > 0 ? (
                      <select
                        value={value}
                        onChange={(e) => setEditMeta((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Select —</option>
                        {allowedValues.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={key === 'as_of_date' ? 'date' : 'text'}
                        value={value}
                        onChange={(e) => setEditMeta((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>
                );
              })}
              {editError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{editError}</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setEditDoc(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMetadata}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeDocuments;
